import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Briefcase, Copy, Download, ExternalLink, Loader2, Search, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState, Field, StatCard } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, canSee, errMsg, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";

const fmtDoc = (d = "") => (d.length === 14 ? d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5") : d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"));
const mesAnterior = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };
const diasPara = (iso) => (iso ? Math.ceil((new Date(iso) - new Date()) / 86400000) : null);

export default function Contabil() {
  const { session } = useAuth();
  const [d, setD] = useState(null);
  const [busca, setBusca] = useState("");
  const [comp, setComp] = useState(mesAnterior());
  const [pedido, setPedido] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/contabil/escritorio/clientes").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const lista = useMemo(() => (d?.clientes || []).filter((c) => {
    const q = busca.trim().toLowerCase();
    return !q || `${c.cliente?.nome} ${c.cliente?.razao_social} ${c.cliente?.documento}`.toLowerCase().includes(q);
  }), [d, busca]);
  if (!canSee(session.user, "contabil")) return <EmptyState icon={Briefcase} title="Painel do escritório contábil" text="Disponível no plano Escritório contábil." />;

  const entrar = async (c) => {
    // abre a aba antes da resposta (o navegador bloqueia janelas abertas depois de esperar)
    const aba = window.open("about:blank", "_blank");
    setBusy(c.id);
    try {
      const { data } = await api.post(`/contabil/escritorio/clientes/${c.id}/entrar`);
      if (aba) aba.location.href = `/contador/entrar/${data.codigo}`;
      else window.location.href = `/contador/entrar/${data.codigo}`;
    } catch (e) { aba?.close(); toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const baixarXml = async (c) => {
    setBusy(`xml-${c.id}`);
    try {
      const r = await api.get(`/contabil/escritorio/clientes/${c.id}/xml`, { params: { competencia: comp }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `xml-${c.cliente?.documento}-${comp}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.response?.status === 403 ? "O cliente não liberou o Fiscal para o escritório" : "Não foi possível gerar o arquivo"); } finally { setBusy(""); }
  };
  const solicitar = async () => {
    try {
      const { data } = await api.post("/contabil/escritorio/solicitar", { documento: pedido });
      toast.success(data.message);
      setPedido(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const ativos = (d?.clientes || []).filter((c) => c.status === "ativo");
  const alertas = ativos.filter((c) => (c.resumo?.notas_com_erro || 0) > 0 || ((diasPara(c.resumo?.certificado_valido_ate) ?? 99) < 30) || c.resumo?.ponto_mes_anterior?.fechado === false);

  return (
    <div className="space-y-6" data-testid="contabil-page">
      <PageHeader eyebrow="Escritório contábil" title="Meus clientes" subtitle="Todos os clientes num lugar só, com o acesso que cada um liberou"
        actions={<>
          {d && <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(d.codigo_escritorio); toast.success("Código copiado"); }}><Copy className="mr-2 h-4 w-4" />Código do escritório: {d.codigo_escritorio}</Button>}
          <Button onClick={() => setPedido("")}><UserPlus className="mr-2 h-4 w-4" />Pedir acesso a um cliente</Button>
        </>} />
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Clientes com acesso" value={ativos.length} icon={Briefcase} />
        <StatCard label="Aguardando o cliente" value={(d?.clientes || []).filter((c) => c.status === "pendente").length} tone="warning" />
        <StatCard label="Pedem atenção" value={alertas.length} icon={AlertTriangle} tone={alertas.length ? "danger" : "success"} hint="Notas com erro, certificado vencendo ou ponto aberto" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar cliente por nome ou CNPJ" className="pl-9" /></div>
        <label className="flex items-center gap-2 text-sm">Mês dos XML<Input type="month" value={comp} onChange={(e) => e.target.value && setComp(e.target.value)} className="w-40" /></label>
      </div>
      {!d ? <p className="text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Carregando…</p> : !lista.length ? (
        <EmptyState icon={Briefcase} title="Nenhum cliente ainda" text={`Passe o código ${d.codigo_escritorio} para o cliente liberar em Sistema → Meu contador, ou peça acesso pelo CNPJ.`} />
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-sm" data-testid="contabil-clientes">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>{["Cliente", "Acesso", "Fiscal", "Ponto", "Último acesso", ""].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
            </thead>
            <tbody>
              {lista.map((c) => {
                const r = c.resumo || {};
                const dc = diasPara(r.certificado_valido_ate);
                return (
                  <tr key={c.id} className="border-t align-top">
                    <td className="px-3 py-2"><b>{c.cliente?.nome}</b><span className="block text-xs text-muted-foreground">{c.cliente?.razao_social} · {fmtDoc(c.cliente?.documento || "")}</span>
                      {r.situacao && !["active", "trial"].includes(r.situacao) && <span className="text-xs text-red-500">Sistema do cliente: {r.situacao}</span>}</td>
                    <td className="px-3 py-2 text-xs">{c.status === "pendente" ? <span className="text-amber-600">Aguardando o cliente aprovar</span> : <>{c.leitura.join(", ")}{c.operar.length ? <span className="block text-emerald-600">opera: {c.operar.join(", ")}</span> : <span className="block text-muted-foreground">somente consulta</span>}</>}</td>
                    <td className="px-3 py-2 text-xs">{c.leitura?.includes("fiscal") && c.status === "ativo" ? <>
                      {r.notas_mes ?? 0} nota(s) no mês{r.notas_com_erro ? <span className="block text-red-500">{r.notas_com_erro} com erro</span> : null}
                      {dc !== null && <span className={`block ${dc < 30 ? "text-red-500" : "text-muted-foreground"}`}>certificado {dc < 0 ? "vencido" : `vence em ${dc} dia(s)`}</span>}</> : "—"}</td>
                    <td className="px-3 py-2 text-xs">{r.ponto_mes_anterior ? (r.ponto_mes_anterior.fechado ? <span className="text-emerald-600">{r.ponto_mes_anterior.competencia} fechado</span> : <span className="text-amber-600">{r.ponto_mes_anterior.competencia} aberto</span>) : "—"}</td>
                    <td className="px-3 py-2 text-xs">{c.ultimo_acesso ? fmtDateTime(c.ultimo_acesso) : "—"}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {c.status === "ativo" && c.leitura.includes("fiscal") && <Button size="sm" variant="outline" className="mr-2" disabled={!!busy} onClick={() => baixarXml(c)}>{busy === `xml-${c.id}` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}XML {comp.slice(5)}/{comp.slice(0, 4)}</Button>}
                      {c.status === "ativo" && <Button size="sm" disabled={!!busy} onClick={() => entrar(c)} data-testid={`contabil-entrar-${c.id}`}><ExternalLink className="mr-1 h-4 w-4" />Entrar</Button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-muted-foreground">"Entrar" abre o sistema do cliente numa aba nova, só com as áreas que ele liberou. Você continua logado no escritório nesta aba. Tudo o que você fizer fica no histórico do cliente.</p>
      <Dialog open={pedido !== null} onOpenChange={(o) => !o && setPedido(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Pedir acesso a um cliente</DialogTitle><DialogDescription>O cliente recebe o pedido em Sistema → Meu contador e escolhe o que liberar.</DialogDescription></DialogHeader>
          <Field label="CNPJ ou CPF do cliente"><Input value={pedido || ""} onChange={(e) => setPedido(e.target.value)} inputMode="numeric" /></Field>
          <DialogFooter><Button onClick={solicitar} disabled={(pedido || "").replace(/\D/g, "").length < 11}>Enviar pedido</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
